import { Suspense, useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, Activity } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useEntitledToolkits } from '@/hooks/useEntitledToolkits';
import { useT } from '@/lib/i18n';
import { buttonClassName } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { Loader } from '@/components/ui/loader';
import { PageTitle } from '@/components/PageTitle';
import { TabBar } from '@/components/TabBar';

export default function Dashboard() {
  const { isGlobalAdmin, claims, profile } = useAuth();
  const { t } = useT();
  const toolkits = useEntitledToolkits();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabsWithDashboard = toolkits.filter(tk => tk.DashboardTab);

  const paramTab = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState<string>(
    () => paramTab ?? tabsWithDashboard[0]?.id ?? '',
  );

  const goToTab = (id: string) => {
    setActiveTab(id);
    setSearchParams({ tab: id }, { replace: true });
  };

  useEffect(() => {
    if (!tabsWithDashboard.find(tk => tk.id === activeTab) && tabsWithDashboard.length > 0) {
      setActiveTab(tabsWithDashboard[0].id);
    }
  }, [tabsWithDashboard, activeTab]);

  if (isGlobalAdmin && !claims.assumedOrgId) {
    return (
      <div className="space-y-6">
        <PageTitle title={t('nav.dashboard')} subtitle={t('dashboard.admin_subtitle')} />
        <EmptyState
          icon={<Activity className="h-5 w-5" />}
          title={t('portal.no_org.title')}
          description={t('portal.no_org.desc')}
          action={
            <Link to="/admin/orgs" className={buttonClassName('primary')}>
              <ArrowRight className="h-4 w-4" /> {t('portal.no_org.cta')}
            </Link>
          }
        />
      </div>
    );
  }

  if (!claims.orgId && !claims.assumedOrgId) {
    return (
      <EmptyState
        icon={<Activity className="h-5 w-5" />}
        title={t('portal.no_org_member.title')}
        description={t('portal.no_org_member.desc')}
      />
    );
  }

  const ActiveTab = tabsWithDashboard.find(tk => tk.id === activeTab)?.DashboardTab;

  return (
    <div className="space-y-6">
      <PageTitle
        title={t('portal.greeting', { name: profile?.displayName ?? '' })}
        subtitle={t('portal.subtitle')}
      />

      <TabBar
        tabs={tabsWithDashboard.map(tk => ({
          id: tk.id,
          label: t(tk.labelKey),
          icon: tk.icon,
        }))}
        activeTab={activeTab}
        onChange={goToTab}
      />

      <Suspense fallback={<Loader label={t('common.loading')} />}>
        {ActiveTab ? <ActiveTab /> : (
          <EmptyState
            icon={<Activity className="h-5 w-5" />}
            title={t('dashboard.no_toolkits.title')}
            description={t('dashboard.no_toolkits.desc')}
            action={<Link to="/toolkits" className={buttonClassName('primary')}>{t('dashboard.no_toolkits.cta')}</Link>}
          />
        )}
      </Suspense>
    </div>
  );
}
